// Platform → Tenants. List + create form (collapsible).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { platformApi } from '../lib/api.js';

export default function PlatformTenants() {
  const [tenants, setTenants] = useState(null);
  const [err, setErr] = useState(null);
  const [showNew, setShowNew] = useState(false);

  async function load() {
    try { const r = await platformApi.tenants(); setTenants(r.tenants); setErr(null); }
    catch (e) { setErr(e.body?.message || e.message); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Tenants</h1>
        <button className="px-3 py-1.5 rounded bg-slate-900 text-white text-sm font-medium"
          onClick={() => setShowNew(s => !s)}>{showNew ? 'Cancel' : '+ New tenant'}</button>
      </div>

      {showNew && <NewTenantForm onCreated={() => { setShowNew(false); load(); }} />}
      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Slug</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Plan</th>
              <th className="px-3 py-2 text-right">Facilities</th>
              <th className="px-3 py-2 text-right">Users</th>
              <th className="px-3 py-2 text-right">Patients</th>
              <th className="px-3 py-2 text-left">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tenants?.map(t => (
              <tr key={t.id} className={`hover:bg-slate-50 ${t.deleted_at ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 font-mono text-xs">
                  <Link to={`/platform/tenants/${t.id}`} className="text-blue-700 hover:underline">{t.slug}</Link>
                </td>
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2"><StatusBadge s={t.status} /></td>
                <td className="px-3 py-2 text-xs">{t.plan}</td>
                <td className="px-3 py-2 text-right tabular-nums">{t.facility_count}</td>
                <td className="px-3 py-2 text-right tabular-nums">{t.user_count}</td>
                <td className="px-3 py-2 text-right tabular-nums">{t.patient_count}</td>
                <td className="px-3 py-2 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {tenants && !tenants.length && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">No tenants yet.</td></tr>
            )}
            {!tenants && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewTenantForm({ onCreated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [f, setF] = useState({
    slug: '', name: '', plan: 'starter', merchant_scope: 'facility',
    initial_facility: { code: '', name: '' },
    initial_admin: { email: '', full_name: '', password: '' },
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const setNested = (group, k, v) => setF(s => ({ ...s, [group]: { ...s[group], [k]: v } }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await platformApi.createTenant(f);
      onCreated();
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
      <h2 className="font-semibold">Provision a new tenant</h2>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <Field label="Slug (subdomain)" hint="2-32 lowercase, dashes ok"
          v={f.slug} on={(v) => set('slug', v.toLowerCase())} mono />
        <Field label="Name (display)" v={f.name} on={(v) => set('name', v)} />
        <Field label="Plan" v={f.plan} on={(v) => set('plan', v)} />
        <Field label="Merchant scope (tenant|facility)"
          v={f.merchant_scope} on={(v) => set('merchant_scope', v)} />
      </div>
      <div className="border-t border-slate-100 pt-3">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Initial facility</div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Facility code (unique)" v={f.initial_facility.code}
            on={(v) => setNested('initial_facility', 'code', v.toUpperCase())} mono />
          <Field label="Facility name" v={f.initial_facility.name}
            on={(v) => setNested('initial_facility', 'name', v)} />
        </div>
      </div>
      <div className="border-t border-slate-100 pt-3">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">First tenant admin</div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <Field label="Email" v={f.initial_admin.email}
            on={(v) => setNested('initial_admin', 'email', v.toLowerCase())} />
          <Field label="Full name" v={f.initial_admin.full_name}
            on={(v) => setNested('initial_admin', 'full_name', v)} />
          <Field label="Temp password (>= 8)" v={f.initial_admin.password} type="password"
            on={(v) => setNested('initial_admin', 'password', v)} />
        </div>
      </div>
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
      <button className="px-4 py-2 rounded bg-slate-900 text-white font-medium" disabled={busy}>
        {busy ? 'Creating…' : 'Create tenant'}
      </button>
    </form>
  );
}

function Field({ label, v, on, hint, mono = false, type = 'text' }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600">{label}</span>
      <input type={type}
        className={`w-full rounded border border-slate-300 p-2 ${mono ? 'font-mono' : ''}`}
        value={v} onChange={e => on(e.target.value)} />
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

function StatusBadge({ s }) {
  const tone = s === 'active' ? 'bg-emerald-100 text-emerald-800'
    : s === 'trial' ? 'bg-blue-100 text-blue-800'
    : s === 'suspended' ? 'bg-amber-100 text-amber-800'
    : 'bg-red-100 text-red-800';
  return <span className={`px-2 py-0.5 rounded text-xs ${tone}`}>{s}</span>;
}
